/**
 * Multi-depot economics — bonuses, structural monthly costs and capacity.
 *
 * Game design (28/05/2026 — updated 28/05/2026 v2 for 6 depots) :
 *
 * The company can own up to 6 depots in TOTAL :
 *   - up to 3 self-OPENED depots (HQ + 2 built via investment),
 *   - up to 3 ACQUIRED depots (from M&A).
 *
 * Each opened depot pays a structural monthly cost AND gives a contract
 * revenue bonus depending on the slot it occupies in the opened sequence.
 * Each acquired depot gives a DIFFERENT bonus tied to the takeover
 * narrative (brand recognition, loyal client base, veteran crew).
 *
 *   Opened slot 1 (HQ)      : 5 → 10 cap via inv-depot-01, free
 *   Opened slot 2 (regional): +8 % revenue on contracts < 200 km, 3 500 €/m
 *   Opened slot 3 (national): +5 % revenue on > 500 km + Export unlock, 4 500 €/m
 *
 *   Acquired #1 (brand) : +10 % local notoriety  (rep boost), 4 000 €/m
 *   Acquired #2 (clients): +5 % revenue on all existing contracts
 *                          (inherited loyal client base), 5 000 €/m
 *   Acquired #3 (veterans): -15 % driver fatigue rate (veteran crew),
 *                          6 000 €/m
 *
 * Force-investment rule (anti-saturation) :
 *   - fleet > 8  AND total depots ≤ 1 : -10 % productivity + monthly
 *     "Plainte voisinage" + refus contrats > 50 000 €
 *   - fleet > 16 AND total depots ≤ 3 : SAME penalties — saturation at
 *     the next layer. Encourages the player to seek M&A growth once their
 *     organic capacity (3 opened depots) is full.
 */
import { getTotalDepotRent } from './depotGeo';

/**
 * 17/06/2026 — Refonte dépôts user. Limites doublées :
 *   - Capacité par dépôt : 5 → 10 camions (DEFAULT_DEPOT_CAPACITY).
 *   - HQ avec upgrade : 10 → 15 camions (HQ_DEPOT_UPGRADED_CAPACITY).
 *   - Max dépôts : 3+3=6 → escalade par phase (5 puis 10 totaux).
 *   - Quai +5 (au lieu de +3) pour rester cohérent avec la nouvelle base.
 */
export const MAX_DEPOTS = 10;          // cap absolu (phase ≥ 4)
export const MAX_DEPOTS_PHASE_3 = 5;   // phase 3 (national)
export const MAX_DEPOTS_PHASE_BASE = 3; // phase 1-2 (local/regional)
export const MAX_OPENED_DEPOTS = 6;   // FR + EU + Maroc + UK ouverts
export const MAX_ACQUIRED_DEPOTS = 4; // 4 concurrents FR rachetables
export const DEFAULT_DEPOT_CAPACITY = 10;
export const HQ_DEPOT_UPGRADED_CAPACITY = 15;
export const QUAI_CAPACITY_BONUS = 5;

export const NEW_DEPOT_OPEN_COST = 35000;
export const MA_INTEGRATION_ONE_SHOT_COST = 8000;

// Monthly structural costs for OPENED depots (index = opened slot).
export const OPENED_DEPOT_MONTHLY_COSTS = [0, 3500, 4500];
// Monthly structural costs for ACQUIRED depots (index = acquired slot).
export const ACQUIRED_DEPOT_MONTHLY_COSTS = [4000, 5000, 6000];

// Bonuses for opened depots, indexed by opened-slot position.
// label/description are i18n keys (resolved at render time by the component).
export const OPENED_DEPOT_BONUSES = [
  {
    labelKey: 'depot_eco_opened_0_label',
    descriptionKey: 'depot_eco_opened_0_desc',
    revenueBonus: 0,
    appliesTo: null,
    unlocks: null,
  },
  {
    labelKey: 'depot_eco_opened_1_label',
    descriptionKey: 'depot_eco_opened_1_desc',
    revenueBonus: 0.08,
    appliesTo: 'regional',
    unlocks: null,
  },
  {
    labelKey: 'depot_eco_opened_2_label',
    descriptionKey: 'depot_eco_opened_2_desc',
    revenueBonus: 0.05,
    appliesTo: 'longHaul',
    unlocks: 'export',
  },
];

// Bonuses for acquired depots, indexed by acquired-slot position.
// These are NARRATIVE-driven bonuses tied to the M&A storyline.
export const ACQUIRED_DEPOT_BONUSES = [
  {
    labelKey: 'depot_eco_acquired_0_label',
    descriptionKey: 'depot_eco_acquired_0_desc',
    reputationMonthly: 10,
    revenueBonusAllContracts: 0,
    fatigueRateMultiplier: 1,
  },
  {
    labelKey: 'depot_eco_acquired_1_label',
    descriptionKey: 'depot_eco_acquired_1_desc',
    reputationMonthly: 0,
    revenueBonusAllContracts: 0.05,
    fatigueRateMultiplier: 1,
  },
  {
    labelKey: 'depot_eco_acquired_2_label',
    descriptionKey: 'depot_eco_acquired_2_desc',
    reputationMonthly: 0,
    revenueBonusAllContracts: 0,
    fatigueRateMultiplier: 0.85,
  },
];

// Force-investment rule constants (extended 28/05/2026 v2)
// 13/06/2026 — Chantier Dépôts : seuil abaissé 8 → 5 camions (demande
// utilisateur : « il faut forcer à investir dans le dépôt à partir de
// 5 camions »).
export const FORCE_INVEST_FLEET_THRESHOLD = 5;
export const FORCE_INVEST_FLEET_THRESHOLD_2 = 16;  // 2nd layer (≤3 depots)
export const FORCE_INVEST_PRODUCTIVITY_PENALTY = 0.10;
export const FORCE_INVEST_REPUTATION_PENALTY = 3;
export const FORCE_INVEST_CONTRACT_VALUE_CAP = 50000;

/**
 * Backward compat — many call sites use ownedDepots as a flat list and
 * read DEPOT_SLOT_BONUSES / DEPOT_MONTHLY_COSTS. We keep those names but
 * make them the OPENED-depot tables (the historical model).
 */
export const DEPOT_SLOT_BONUSES = OPENED_DEPOT_BONUSES;
export const DEPOT_MONTHLY_COSTS = OPENED_DEPOT_MONTHLY_COSTS;

/**
 * Split an ownedDepots array into [openedDepots, acquiredDepots] using
 * the `acquired` flag stored on M&A depots (added by acquisitionSlice).
 * Robust to legacy depots that don't have the flag — they are treated as
 * opened. The order WITHIN each sub-list is preserved (= opened/acquired
 * slot index).
 */
export function splitDepots(ownedDepots = []) {
  const opened = [];
  const acquired = [];
  ownedDepots.forEach(d => {
    if (d && d.acquired) acquired.push(d); else opened.push(d);
  });
  return { opened, acquired };
}

/**
 * Returns the structural monthly cost across ALL depots.
 * 13/06/2026 — Chantier Dépôts Phase 1 : délègue à la grille de loyers par
 * ville (data/depotGeo.js). L'ancienne table par slot (0/3500/4500 +
 * 4000/5000/6000) est remplacée par : base ville (A 4500 / B 3500 / C 2500 /
 * EU 5500) × facteur taille de terrain (investissements) × aides à
 * l'implantation. Signature étendue, rétro-compatible (1 seul argument →
 * villes par défaut).
 */
export function getDepotMonthlyCost(ownedDepots = [], activeInvestments = {}, company = null, totalDays = 0) {
  return getTotalDepotRent(ownedDepots, activeInvestments, company, totalDays);
}

/**
 * Revenue multiplier given a contract's distance — aggregates both
 * opened depot bonuses (slot-based) AND acquired depot bonuses
 * (the "loyal clients" perk gives +5 % across the board).
 */
export function getDepotContractMultiplier(ownedDepots = [], distanceKm = 0) {
  const { opened, acquired } = splitDepots(ownedDepots);
  let multiplier = 1;
  opened.forEach((_, idx) => {
    const cfg = OPENED_DEPOT_BONUSES[idx];
    if (!cfg || !cfg.revenueBonus) return;
    if (cfg.appliesTo === 'regional' && distanceKm > 0 && distanceKm < 200) multiplier += cfg.revenueBonus;
    if (cfg.appliesTo === 'longHaul' && distanceKm > 500) multiplier += cfg.revenueBonus;
  });
  acquired.forEach((_, idx) => {
    const cfg = ACQUIRED_DEPOT_BONUSES[idx];
    if (!cfg) return;
    if (cfg.revenueBonusAllContracts) multiplier += cfg.revenueBonusAllContracts;
  });
  return multiplier;
}

/**
 * Driver fatigue rate multiplier from acquired depot veteran bonus.
 * Returns 1 if no veteran depot acquired. Used in fleetSlice / coreSlice.
 */
export function getDriverFatigueMultiplier(ownedDepots = []) {
  const { acquired } = splitDepots(ownedDepots);
  let mult = 1;
  acquired.forEach((_, idx) => {
    const cfg = ACQUIRED_DEPOT_BONUSES[idx];
    if (cfg && typeof cfg.fatigueRateMultiplier === 'number') {
      mult *= cfg.fatigueRateMultiplier;
    }
  });
  return mult;
}

/**
 * Monthly reputation bonus from acquired depots (brand recognition perk).
 * Applied at month close.
 */
export function getDepotMonthlyReputationBonus(ownedDepots = []) {
  const { acquired } = splitDepots(ownedDepots);
  let bonus = 0;
  acquired.forEach((_, idx) => {
    const cfg = ACQUIRED_DEPOT_BONUSES[idx];
    if (cfg && cfg.reputationMonthly) bonus += cfg.reputationMonthly;
  });
  return bonus;
}

export function hasExportUnlock(ownedDepots = []) {
  const { opened } = splitDepots(ownedDepots);
  return opened.length >= 3;
}

/**
 * 17/06/2026 — Occupation effective d'un dépôt (refonte user).
 * 23/07/2026 — Règle simplifiée (choix user 2b) : seuls les camions
 * PHYSIQUEMENT stationnés comptent dans la jauge. Un camion en mission
 * (contrat régulier OU ponctuel) est sur la route : il ne consomme pas
 * d'emplacement. Comptent : idle, maintenance/panne, transfert entrant,
 * livraison (camion neuf en approche).
 *
 * @param {string} depotId
 * @param {Array}  vehicles
 * @param {Array}  activeContracts — conservé pour compat de signature.
 */
export function getDepotOccupancy(depotId, vehicles = [], activeContracts = []) {
  const target = depotId || 'depot-hq';
  return (vehicles || []).filter(v => {
    if (v.status === 'sold' || v.status === 'selling') return false;
    if ((v.depotId || 'depot-hq') !== target) return false;
    // En mission (n'importe quel contrat) → sur la route, ne compte pas.
    if (v.assignedContractId || v.status === 'active') return false;
    return true;
  }).length;
}

/**
 * 17/06/2026 — Cap dynamique du nombre de dépôts par phase de jeu (refonte
 * user). Progressivité : 3 → 5 → 10 selon la maturité de l'entreprise.
 *   - phase 1-2 : 3 dépôts (équilibrage initial, force l'investissement)
 *   - phase 3   : 5 dépôts (national, ouverture du tissu)
 *   - phase 4+  : 10 dépôts (international, scale maximal)
 */
export function getMaxDepotsForPhase(phase = 1) {
  if (phase >= 4) return MAX_DEPOTS;
  if (phase === 3) return MAX_DEPOTS_PHASE_3;
  return MAX_DEPOTS_PHASE_BASE;
}

/**
 * Two-layer force-investment rule :
 *   - layer 1 : fleet > 8 AND total depots ≤ 1
 *   - layer 2 : fleet > 16 AND total depots ≤ 3
 * Both trigger the same 3 penalties.
 */
export function isForceInvestActive(state) {
  const fleetCount = (state.vehicles || []).length;
  const depotCount = (state.ownedDepots || []).length;
  if (fleetCount > FORCE_INVEST_FLEET_THRESHOLD && depotCount <= 1) return true;
  if (fleetCount > FORCE_INVEST_FLEET_THRESHOLD_2 && depotCount <= 3) return true;
  return false;
}
